export type EmailHistoryFilters = {
  q?:string; person?:string; contact?:string; projectNumber?:string; projectId?:number;
  from?:string; to?:string; cursor?:string; pageSize?:number;
};
export type EmailHistoryItem = {
  emailMessageId:number; subject:string; from:{email:string|null;name:string|null};
  to:Array<{email:string|null;name?:string|null}>; cc:Array<{email:string|null;name?:string|null}>;
  direction:"inbound"|"outbound"|"unknown"; occurredAt:string|null; folder:string|null;
  currentText:string; quotedTextFolded:boolean; textTruncated:boolean;
  attachments:Array<{name:string;size:number|null}>; projects:Array<{projectId:number;projectNumber:string|null}>;
  sourceRef:{emailMessageId:number;sourceMailbox:string}; openUrl:string|null;
  relationship?:"anchor"|"direct_reply"|"conversation"|"reply_reference";
};
export type EmailHistoryPage = {
  items:EmailHistoryItem[]; nextCursor:string|null; searchMode:"literal_keywords";
  coverage:{scope:"authorized_imported_mailbox_records";archiveComplete:false;oldestAt:string|null;newestAt:string|null;
    folders:string[];hasMore:boolean;notice:string};
  projectFilter?:{projectId:number;projectNumber:string|null};
  anchorId?:number; relationPolicy?:"reply_references_or_conversation_only"; conflictingProjectsExcluded?:boolean;
};
export type EmailHistoryClient = {
  search:(filters:EmailHistoryFilters,signal?:AbortSignal)=>Promise<EmailHistoryPage>;
  trail:(emailMessageId:number,cursor?:string,signal?:AbortSignal)=>Promise<EmailHistoryPage>;
  openOriginal:(emailMessageId:number,signal?:AbortSignal)=>Promise<{openUrl:string}>;
};
const positive=(value:unknown)=>Number.isSafeInteger(value) && Number(value)>0;
const text=(value:unknown,max=50000):value is string=>typeof value==="string" && value.length<=max;
const nullableText=(value:unknown,max=50000)=>value===null || text(value,max);
const record=(value:any)=>Boolean(value && typeof value==="object" && !Array.isArray(value));
const contact=(value:any)=>record(value) && nullableText(value.email,254) && (value.name==null || text(value.name,500));
export function safeEmailHistoryOpenUrl(value:unknown):string|null {
  if(typeof value!=="string") return null;
  try {
    const url=new URL(value);
    const owaKeys=new Set(["ItemID","exvsurl","viewmodel"]);
    const owa=url.pathname==="/owa/" && Boolean(url.searchParams.get("ItemID")) && url.searchParams.get("viewmodel")==="ReadMessageItem"
      && [...url.searchParams.keys()].every(key=>owaKeys.has(key) && url.searchParams.getAll(key).length===1);
    return url.protocol==="https:" && !url.username && !url.password
      && !url.hash && (!url.port || url.port==="443")
      && ["outlook.office.com","outlook.office365.com","outlook.live.com"].includes(url.hostname.toLowerCase())
      && (/^\/mail\/(?:deeplink\/read|inbox\/id\/)/.test(url.pathname) || owa)
      && !/[?&](?:sig|token|access_token)=/i.test(url.search) ? value : null;
  } catch {return null;}
}
/** Validate the shared read-only contract before rendering mailbox evidence. */
export function validateEmailHistoryPage(value:unknown,anchorId?:number):EmailHistoryPage {
  const page=value as EmailHistoryPage,coverage=page?.coverage;
  if(!record(page) || !Array.isArray(page.items) || page.items.length>100 || !nullableText(page.nextCursor,8192)
    || page.nextCursor==="" || page.searchMode!=="literal_keywords" || !record(coverage)
    || coverage.scope!=="authorized_imported_mailbox_records" || coverage.archiveComplete!==false
    || !nullableText(coverage.oldestAt,150) || !nullableText(coverage.newestAt,150)
    || !Array.isArray(coverage.folders) || coverage.folders.length>200 || coverage.folders.some(folder=>!text(folder,500))
    || typeof coverage.hasMore!=="boolean" || coverage.hasMore!==Boolean(page.nextCursor) || !text(coverage.notice,1500)
    || page.projectFilter!==undefined && (!record(page.projectFilter) || !positive(page.projectFilter.projectId) || !nullableText(page.projectFilter.projectNumber,100))
    || anchorId!==undefined && (page.anchorId!==anchorId || page.relationPolicy!=="reply_references_or_conversation_only" || typeof page.conflictingProjectsExcluded!=="boolean")
    || page.items.some(item=>!record(item) || !positive(item.emailMessageId) || !text(item.subject,1000) || !contact(item.from)
      || !Array.isArray(item.to) || item.to.length>200 || !item.to.every(contact) || !Array.isArray(item.cc) || item.cc.length>200 || !item.cc.every(contact)
      || !["inbound","outbound","unknown"].includes(item.direction) || !nullableText(item.occurredAt,150) || !nullableText(item.folder,500)
      || !text(item.currentText) || typeof item.quotedTextFolded!=="boolean" || typeof item.textTruncated!=="boolean"
      || !Array.isArray(item.attachments) || item.attachments.length>200 || item.attachments.some(file=>!record(file) || !text(file.name,1000)
        || file.size!==null && (!Number.isSafeInteger(file.size) || file.size<0))
      || !Array.isArray(item.projects) || item.projects.length>100 || item.projects.some(project=>!record(project) || !positive(project.projectId) || !nullableText(project.projectNumber,100))
      || !record(item.sourceRef) || item.sourceRef.emailMessageId!==item.emailMessageId || !text(item.sourceRef.sourceMailbox,254)
      || item.openUrl!==null && !safeEmailHistoryOpenUrl(item.openUrl)
      || item.relationship!==undefined && !["anchor","direct_reply","conversation","reply_reference"].includes(item.relationship))) {
    throw new Error("Email history could not be verified. Retry the search; no project or action has been changed.");
  }
  return page;
}
export function createEmailHistoryClient(read:(path:string,signal?:AbortSignal)=>Promise<unknown>):EmailHistoryClient {
  return {
    async search(filters,signal) {
      const params=new URLSearchParams();
      for(const name of ["q","person","contact","projectNumber","projectId","from","to","cursor","pageSize"] as const) {
        const value=filters[name];if(value!==undefined && String(value).trim()) params.set(name,String(value).trim());
      }
      return validateEmailHistoryPage(await read(`/api/v1/email/history/search?${params}`,signal));
    },
    async trail(emailMessageId,cursor,signal) {
      if(!positive(emailMessageId)) throw new Error("Choose a recorded email before opening its trail.");
      const params=new URLSearchParams({pageSize:"25"});if(cursor)params.set("cursor",cursor);
      return validateEmailHistoryPage(await read(`/api/v1/email/history/${emailMessageId}/trail?${params}`,signal),emailMessageId);
    },
    async openOriginal(emailMessageId,signal) {
      if(!positive(emailMessageId))throw new Error("Choose a recorded email before opening its original.");
      const result=await read(`/api/v1/email/history/${emailMessageId}/source`,signal) as {openUrl?:unknown};
      if(!record(result) || Object.keys(result).length!==1 || !safeEmailHistoryOpenUrl(result.openUrl)) {
        throw new Error("The original email link could not be verified. Open the email directly in Outlook.");
      }
      return {openUrl:result.openUrl as string};
    },
  };
}
export class EmailHistoryReadError extends Error {}
export function emailHistoryRequestError(status:number,code?:string):Error {
  const message=code==="invalid_cursor" ? "This results page expired or belongs to another search. Run the search again."
    : status===401 || status===403 ? "Email history is unavailable with your current mailbox or project access. Sign in with the authorized account."
    : status===404 ? "This email is no longer available with your current access."
    : status===409 ? "The original email could not be uniquely identified. Open it directly in Outlook."
    : status===400 ? "Check the search fields and date range, then search again."
    : "Email history is temporarily unavailable. Retry the search; this is not a no-results response.";
  return new EmailHistoryReadError(message);
}
